// The gust's grains: the 2026 version's x20 sweep, with its grain physics untouched
// (`GRAIN_GLSL` and `UPDATE_SHADER` are copied verbatim from the old site). There's one grain
// under each cell of the grains' own grid, and one simulation per live wave. Nothing
// scatters. The site has them switched off for now (`GUST_GRAINS` in field.ts), but the code
// stays.
//
// Each wave's layer of `texture` holds, for each cell:
// - R: the time the wave reached it
// - G: the dust over it right now
// - B: its fetch, the vortex's ground wind that has passed over it so far
// - A: the time its grain got blown `SWEEP_UNITS` from home
//
// A cell counts as reached once its fetch passes a threshold, or half of that once its grain
// is swept, so the swept grains carry the change a little ahead of the wind. The site's field
// draws them: its lift pass turns R into the palette blend and the parts' ages, and G into the
// dust (field/field-shaders.ts).
//
//   wave starts ──► slot: two state buffers, its layer cleared (R = A = `UNREACHED`)
//   each frame  ──► step the physics to the wave's time (4ms steps, transform feedback)
//               ──► mark A (min) at each swept grain's home cell, G (sum) under the dust
//               ──► add the frame's wind to B; R = now where B passes the threshold, or
//                   `CARRY` of it with A set
//   wave ends   ──► the slot is freed

import type { GustWave } from "../core/gust.js";
import { MAX_LIVE_RIPPLES } from "../core/ripple.js";
import { clamp01, hash1 } from "../math.js";
import { GrainSlots } from "./grain-slots.js";
import { createShaderProgram } from "./shader-program.js";

/** How many waves the grains can follow at once: every live wave. */
export const GRAIN_SLOTS = MAX_LIVE_RIPPLES;
/** The time that marks a cell the wave hasn't reached yet, or a grain that isn't swept. */
export const UNREACHED = 60000;

// The sweep's timing: 3s of its simulation per wave life, in 4ms steps. A slow frame takes at
// most `FRAME_STEPS` longer steps instead. With fixed 4ms steps, the slower the frames, the
// more steps each one needed, up to 44 a frame on a software GPU (I measured wave frames 35%
// slower there). At 60 frames a second, a frame takes about 7 steps of 4ms, same as the old
// sweep. The physics holds up fine with longer steps: its rates are exponential decays or
// clamped chances.
const SIM_SECONDS = 3;
const STEP_SECONDS = 0.004;
const FRAME_STEPS = 10;
// The farthest viewport corner from the butterfly, in the sweep simulation's units. A unit is
// that corner's distance divided by this, so the gust covers a viewport of any shape within
// its life. I tuned it at 1280 x 800 with a unit equal to the viewport's height, where that
// corner was 661px across and 626px down.
const CORNER_UNITS = Math.hypot(661, 626) / 800;
// How far a grain has to be blown from home before its cell counts as swept, in units: 16px at
// 1280 x 800, where I picked it. Measuring in units keeps the front's shape on a bigger
// viewport, where pixels would count grains as swept too soon, ahead of the wind.
const SWEEP_UNITS = 16 / 800;
// The fetch that reaches a cell (the ground wind's speed integrated over simulation time, in
// units), give or take half of `FETCH_NOISE`, from a per-cell noise. I measured it, and it
// keeps pace with the grains. From `LATE_START` to `LATE_END` seconds, the threshold drops by
// `LATE_DROP`, so the front still reaches the farthest corner before the life ends, after the
// wind has died down. A cell whose grain is swept only needs `CARRY` of it.
const WIND_FETCH = 0.032;
const FETCH_NOISE = 0.3;
const LATE_START = 1.8;
const LATE_END = 2.7;
const LATE_DROP = 0.97;
const CARRY = 0.5;
// The grains' grid is the field's plus this fraction of its size on each side, so the waves
// survive a small resize. On a phone, the toolbar hiding as you scroll shifts the centered
// field grid.
const HEADROOM = 0.1;
const STATE_FLOATS = 12;

const GRAIN_GLSL = `
const float GRAIN_SPREAD = 0.45;

float hash11(float value) {
  value = fract(value * 0.1031);
  value *= value + 33.33;
  value *= value + value;
  return fract(value);
}

float gaussian(float seed, float offset) {
  return (hash11(seed + offset) + hash11(seed + offset + 17.17) + hash11(seed + offset + 43.71) - 1.5) * 2.0;
}

float grainScale(float seed) {
  return exp(GRAIN_SPREAD * gaussian(seed, 3.7));
}
`;

const UPDATE_SHADER = `#version 300 es
precision highp float;

in vec4 aState0;
in vec4 aState1;
in vec4 aState2;
uniform float uTime;
uniform float uDeltaTime;
uniform vec2 uRingStart;
uniform vec2 uRingDrift;
out vec4 vState0;
out vec4 vState1;
out vec4 vState2;

const float GUST = 3.0;
const float K0 = 0.036;
const float CORE_RADIUS = 0.055;
const float RING_RADIUS = 0.18;
const float RING_EXPANSION = 0.10;
const float RING_EXPANSION_TIME = 0.5;
const float IMPACT_TIME = 0.2;
const float START_HEIGHT = 0.6;
const float DESCENT_TIME = 0.12;
const float BUILD_TIME = 0.06;
const float DECAY_TIME = 3.0;
const float REBOUND_TIME = 1.5;
const float REBOUND_HEIGHT = 0.4;
const float REBOUND_RISE_TIME = 0.5;
const float REBOUND_DECAY_TIME = 0.7;
const float WAVE_AMPLITUDE = 0.02;
const float WAVE_TIME = 0.5;
const float PICKUP = 0.01;
const float PICKUP_RATE = 10.0;
const float CREEP = 0.4;
const float RESPONSE_TIME = 0.03;
const float GRAVITY = 2.0;
const float KICK = 1.0;
const float BOUNCE = 0.35;
const float TURBULENCE = 0.35;
const float TURBULENCE_FLOOR = 0.02;
const float GROUND_TURBULENCE = 0.05;
const float EDDY_TIME = 0.15;

${GRAIN_GLSL}
void vortexField(float radius, float height, float ringRadius, float coreHeight, float circulation, out float radialSpeed, out float verticalSpeed) {
  float nearRadius = radius - ringRadius;
  float farRadius = radius + ringRadius;
  float above = height - coreHeight;
  float image = height + coreHeight;
  float core2 = CORE_RADIUS * CORE_RADIUS;
  radialSpeed = 0.0;
  verticalSpeed = 0.0;
  float divisor = nearRadius * nearRadius + above * above + core2;
  radialSpeed -= circulation * above / divisor;
  verticalSpeed += circulation * nearRadius / divisor;
  divisor = nearRadius * nearRadius + image * image + core2;
  radialSpeed += circulation * image / divisor;
  verticalSpeed -= circulation * nearRadius / divisor;
  divisor = farRadius * farRadius + above * above + core2;
  radialSpeed += circulation * above / divisor;
  verticalSpeed -= circulation * farRadius / divisor;
  divisor = farRadius * farRadius + image * image + core2;
  radialSpeed -= circulation * image / divisor;
  verticalSpeed += circulation * farRadius / divisor;
}

void main() {
  float dt = uDeltaTime;
  vec2 position = aState0.xy;
  float height = aState0.z;
  float airborne = aState0.w;
  vec3 velocity = aState1.xyz;
  float airTime = aState1.w;
  vec3 eddy = aState2.xyz;
  float seed = aState2.w;
  float grainSize = grainScale(seed);
  float pickupThreshold = PICKUP * mix(0.6, 1.4, hash11(seed + 5.1)) * sqrt(grainSize);
  float responseTime = RESPONSE_TIME * grainSize * grainSize;

  float circulation = GUST * K0 * (1.0 - exp(-uTime / BUILD_TIME)) * exp(-uTime / DECAY_TIME);
  float coreHeight = CORE_RADIUS + (START_HEIGHT - CORE_RADIUS) * exp(-uTime / DESCENT_TIME);
  if (uTime > REBOUND_TIME) {
    float reboundTime = uTime - REBOUND_TIME;
    circulation *= exp(-reboundTime / REBOUND_DECAY_TIME);
    coreHeight += REBOUND_HEIGHT * (1.0 - exp(-reboundTime / REBOUND_RISE_TIME));
  }
  float ringRadius = RING_RADIUS;
  float ringWave = 0.0;
  if (uTime > IMPACT_TIME) {
    float impactTime = uTime - IMPACT_TIME;
    ringRadius += RING_EXPANSION * (1.0 - exp(-impactTime / RING_EXPANSION_TIME));
    ringWave = WAVE_AMPLITUDE * (1.0 - exp(-impactTime / WAVE_TIME));
  }
  vec2 ringCenter = uRingStart + uRingDrift * uTime;
  vec2 delta = position - ringCenter;
  float radius = length(delta) + 0.000001;
  vec2 radial = delta / radius;
  vec2 tangent = vec2(-radial.y, radial.x);
  float angle = atan(delta.y, delta.x);
  float waveCount = 5.0 + floor(hash11(seed + 7.3) * 3.0);
  float localRingRadius = ringRadius * (1.0 + ringWave * sin(waveCount * angle + seed * 6.2831853));
  float radialSpeed;
  float verticalSpeed;
  vortexField(radius, height, localRingRadius, coreHeight, circulation, radialSpeed, verticalSpeed);
  float eventStep = floor(uTime / max(dt, 0.000001));

  if (airborne < 0.5) {
    float groundDecay = dt / EDDY_TIME;
    float groundAmplitude = GROUND_TURBULENCE * (circulation / K0) * sqrt(2.0 * groundDecay);
    eddy.x += -eddy.x * groundDecay + groundAmplitude * gaussian(seed, eventStep + 11.0);
    float surfaceSpeed = abs(radialSpeed + eddy.x);
    if (surfaceSpeed > pickupThreshold) {
      position += CREEP * radialSpeed * dt * radial;
      float pickupChance = clamp(PICKUP_RATE * dt * (surfaceSpeed / pickupThreshold - 1.0), 0.0, 1.0);
      if (hash11(seed * 71.9 + eventStep * 0.113) < pickupChance) {
        airborne = 1.0;
        eddy = vec3(0.0);
        velocity.xy = 0.3 * radialSpeed * radial;
        velocity.z = KICK * surfaceSpeed * mix(0.5, 1.5, hash11(seed + eventStep + 19.0));
      }
    }
  } else {
    float speed = length(vec2(radialSpeed, verticalSpeed));
    float turbulenceSigma = TURBULENCE * speed + TURBULENCE_FLOOR;
    float turbulenceDecay = dt / EDDY_TIME;
    float turbulenceAmplitude = turbulenceSigma * sqrt(2.0 * turbulenceDecay);
    eddy.x += -eddy.x * turbulenceDecay + turbulenceAmplitude * gaussian(seed, eventStep + 23.0);
    eddy.y += -eddy.y * turbulenceDecay + turbulenceAmplitude * gaussian(seed, eventStep + 37.0);
    eddy.z += -eddy.z * turbulenceDecay + turbulenceAmplitude * gaussian(seed, eventStep + 51.0);
    vec2 airVelocity = (radialSpeed + eddy.x) * radial + eddy.y * tangent;
    float response = 1.0 - exp(-dt / responseTime);
    velocity.xy += (airVelocity - velocity.xy) * response;
    velocity.z += (verticalSpeed + eddy.z - velocity.z) * response;
    velocity.z -= GRAVITY * dt;
    position += velocity.xy * dt;
    height += velocity.z * dt;
    airTime += dt;
    if (height <= 0.0) {
      height = 0.0;
      float bounceChance = hash11(seed * 97.1 + eventStep * 0.071);
      if (bounceChance < 0.4 && velocity.z < -0.1) {
        velocity.z = -BOUNCE * velocity.z;
        velocity.xy *= 0.7;
      } else {
        airborne = 0.0;
        velocity = vec3(0.0);
      }
    }
  }

  vState0 = vec4(position, height, airborne);
  vState1 = vec4(velocity, airTime);
  vState2 = vec4(eddy, seed);
  gl_Position = vec4(0.0);
}`;

// Marks each grain in its wave's layer, one point per grain.
// - Mode 0: at its home cell, the time it got blown `SWEEP_UNITS` from home (A, blended to
//   the minimum).
// - Mode 1: at the cell it's over now, the dust it shows (G, summed). Like the old sweep, a
//   grain fades with its air time and its height.
const MARK_VERTEX = `#version 300 es
precision highp float;
in vec4 aState0;
in vec4 aState1;
in vec4 aState2;
uniform int uMode;
uniform float uTime;
uniform float uPxPerUnit;
uniform vec2 uStartOrigin;
uniform vec2 uGrid;
uniform vec2 uCell;
uniform vec2 uGridOrigin;
uniform float uSweep;
out float vValue;
${GRAIN_GLSL}
void main() {
  gl_PointSize = 1.0;
  int cols = int(uGrid.x);
  vec2 homeCell = vec2(float(gl_VertexID % cols), float(gl_VertexID / cols)) + 0.5;
  vec2 cell;
  if (uMode == 0) {
    vec2 home = (uGridOrigin + homeCell * uCell - uStartOrigin) / uPxPerUnit;
    if (distance(aState0.xy, home) < uSweep) {
      gl_Position = vec4(2.0, 2.0, 0.0, 1.0);
      return;
    }
    vValue = uTime;
    cell = homeCell;
  } else {
    if (aState0.w < 0.5) {
      gl_Position = vec4(2.0, 2.0, 0.0, 1.0);
      return;
    }
    float grainSize = grainScale(aState2.w);
    float dispersion = max(0.0, 1.0 - aState1.w / (0.8 * grainSize));
    float heightFade = 1.0 / (1.0 + pow(aState0.z / 0.15, 2.0));
    vValue = dispersion * heightFade;
    cell = (uStartOrigin + aState0.xy * uPxPerUnit - uGridOrigin) / uCell;
  }
  gl_Position = vec4(cell / uGrid * 2.0 - 1.0, 0.0, 1.0);
}`;

const MARK_FRAGMENT = `#version 300 es
precision highp float;
in float vValue;
out vec4 outColor;
void main() { outColor = vec4(vValue); }`;

const QUAD_VERTEX = `#version 300 es
void main() {
  gl_Position = vec4(vec2(gl_VertexID % 2, gl_VertexID / 2) * 4.0 - 1.0, 0.0, 1.0);
}`;

// The area a wave has reached, read from a copy of its layer. Each cell adds the frame's
// ground wind to its fetch (B), and the wave reaches it (R = now) once the fetch passes the
// cell's threshold, or `CARRY` of it once its grain is swept (A).
const REGION_FRAGMENT = `#version 300 es
precision highp float;
uniform sampler2D uMarks;
uniform float uTime;
uniform float uPrevTime;
uniform vec2 uCell;
uniform vec2 uGridOrigin;
uniform vec2 uStartOrigin;
uniform float uPxPerUnit;
uniform vec2 uRingStart;
uniform vec2 uRingDrift;
uniform vec2 uLobes;
out vec4 outColor;

// The speed of the vortex's ground wind at p (in units from where the butterfly was when the
// wave started) at time t. It's the same vortex as UPDATE_SHADER, values copied, plus the
// wave's lobes. At ground level, each ring and its mirror image cancel the vertical wind and
// double the radial wind.
float groundWind(vec2 p, float t) {
  float circulation = 3.0 * 0.036 * (1.0 - exp(-t / 0.06)) * exp(-t / 3.0);
  float coreHeight = 0.055 + (0.6 - 0.055) * exp(-t / 0.12);
  if (t > 1.5) {
    float rebound = t - 1.5;
    circulation *= exp(-rebound / 0.7);
    coreHeight += 0.4 * (1.0 - exp(-rebound / 0.5));
  }
  float ringRadius = 0.18;
  float ringWave = 0.0;
  if (t > 0.2) {
    float impact = t - 0.2;
    ringRadius += 0.1 * (1.0 - exp(-impact / 0.5));
    ringWave = 0.02 * (1.0 - exp(-impact / 0.5));
  }
  vec2 delta = p - (uRingStart + uRingDrift * t);
  float local = ringRadius * (1.0 + ringWave * sin(uLobes.x * atan(delta.y, delta.x) + uLobes.y));
  float near = length(delta) - local;
  float far = length(delta) + local;
  float h2 = coreHeight * coreHeight + 0.055 * 0.055;
  return abs(2.0 * circulation * coreHeight * (1.0 / (near * near + h2) - 1.0 / (far * far + h2)));
}

void main() {
  ivec2 cell = ivec2(gl_FragCoord.xy);
  vec4 own = texelFetch(uMarks, cell, 0);
  vec2 p = ((vec2(cell) + 0.5) * uCell + uGridOrigin - uStartOrigin) / uPxPerUnit;
  // The frame's wind, sampled a quarter and three quarters of the way through it.
  float dt = uTime - uPrevTime;
  float fetch = own.b + 0.5 * dt * (groundWind(p, uPrevTime + 0.25 * dt) + groundWind(p, uPrevTime + 0.75 * dt));
  outColor = vec4(own.rg, fetch, own.a);
  if (own.r <= uTime) return;
  float noise = fract(sin(dot(vec2(cell), vec2(12.9898, 78.233))) * 43758.5453);
  float threshold = ${WIND_FETCH.toFixed(3)}
    * (1.0 + ${FETCH_NOISE.toFixed(2)} * (noise - 0.5))
    * (1.0 - ${LATE_DROP.toFixed(2)} * smoothstep(${LATE_START.toFixed(1)}, ${LATE_END.toFixed(1)}, uTime));
  bool swept = own.a <= uTime;
  if (fetch >= threshold || (swept && fetch >= ${CARRY.toFixed(1)} * threshold)) outColor.r = uTime;
}`;

const DISCARD_FRAGMENT = `#version 300 es
precision mediump float;
out vec4 outColor;
void main() { outColor = vec4(0.0); }`;

/** A grid of cells, in viewport CSS pixels. */
export interface GrainGrid {
  cols: number;
  rows: number;
  cellW: number;
  cellH: number;
  originX: number;
  originY: number;
}

/** The viewport's size, in CSS pixels. */
export interface GrainViewport {
  readonly width: number;
  readonly height: number;
}

/** A live wave, as the grains see it: its gust (the drift and the lobes), its id, where the
    butterfly was when it started (in viewport CSS pixels), and the fraction of its life gone
    by, 0 to 1. */
export interface GrainWave extends GustWave {
  readonly id: number;
  readonly originX: number;
  readonly originY: number;
  readonly life: number;
}

// One slot's GPU state: its simulation's two state buffers (ping-pong) and their vertex arrays.
interface Slot {
  buffers: [WebGLBuffer, WebGLBuffer];
  updateArrays: [WebGLVertexArrayObject, WebGLVertexArrayObject];
  markArrays: [WebGLVertexArrayObject, WebGLVertexArrayObject];
  current: 0 | 1;
  // The simulation's time now, and at the last mark.
  time: number;
  prevTime: number;
  pxPerUnit: number;
  // Where the butterfly was when the wave started, in viewport CSS pixels. It's the
  // simulation's origin.
  startX: number;
  startY: number;
  ringStart: [number, number];
  ringDrift: [number, number];
  // The wave's lobes: how many, and their phase.
  lobes: [number, number];
}

/** One grain simulation per live wave, with its marks in a texture array. */
export interface GustGrains {
  /** RGBA16F on the grains' grid, one layer per slot. R is when the wave reached each cell, G
      its dust right now, B its fetch, and A when its grain got swept. */
  readonly texture: WebGLTexture;
  /** Where the field's grid sits inside the grains' grid, in cells (a field cell plus this is
      its grain's cell). Valid after `update`. */
  readonly offset: { readonly x: number; readonly y: number };
  /** A live wave's slot (its layer), or -1. Valid after `update`. */
  slotOf(id: number): number;
  /** A live wave's simulation time, or 0. Valid after `update`. */
  timeOf(id: number): number;
  /** Starts, advances, and ends the waves' simulations, and marks their layers, for the
      field's grid. */
  update(waves: readonly GrainWave[], field: GrainGrid, viewport: GrainViewport): void;
}

function sameGrid(a: GrainGrid, b: GrainGrid): boolean {
  return (
    a.cols === b.cols &&
    a.rows === b.rows &&
    a.cellW === b.cellW &&
    a.cellH === b.cellH &&
    a.originX === b.originX &&
    a.originY === b.originY
  );
}

/** Sets up the grains in the field's context, with their texture array bound to texture
    `unit`. The three programs link in parallel, off the main thread where the browser can.
    Rejects if a program doesn't link (after deleting the ones that did), or if the context
    can't render to float textures. Losing the field's context frees them. */
export async function createGustGrains(
  gl: WebGL2RenderingContext,
  unit: number,
): Promise<GustGrains> {
  if (!gl.getExtension("EXT_color_buffer_float")) throw new Error("Grains: no float targets");
  const linked = await Promise.allSettled([
    createShaderProgram(gl, UPDATE_SHADER, DISCARD_FRAGMENT, undefined, [
      "vState0",
      "vState1",
      "vState2",
    ]),
    createShaderProgram(gl, MARK_VERTEX, MARK_FRAGMENT),
    createShaderProgram(gl, QUAD_VERTEX, REGION_FRAGMENT),
  ]);
  const [updateLink, markLink, regionLink] = linked;
  if (
    updateLink.status === "rejected" ||
    markLink.status === "rejected" ||
    regionLink.status === "rejected"
  ) {
    for (const result of linked) if (result.status === "fulfilled") gl.deleteProgram(result.value);
    throw linked.find((result): result is PromiseRejectedResult => result.status === "rejected")
      ?.reason;
  }
  const updateProgram = updateLink.value;
  const markProgram = markLink.value;
  const regionProgram = regionLink.value;
  const at = (program: WebGLProgram, name: string) => gl.getUniformLocation(program, name);
  const u = {
    time: at(updateProgram, "uTime"),
    delta: at(updateProgram, "uDeltaTime"),
    ringStart: at(updateProgram, "uRingStart"),
    ringDrift: at(updateProgram, "uRingDrift"),
  };
  const m = {
    mode: at(markProgram, "uMode"),
    time: at(markProgram, "uTime"),
    pxPerUnit: at(markProgram, "uPxPerUnit"),
    startOrigin: at(markProgram, "uStartOrigin"),
    grid: at(markProgram, "uGrid"),
    cell: at(markProgram, "uCell"),
    gridOrigin: at(markProgram, "uGridOrigin"),
    sweep: at(markProgram, "uSweep"),
  };
  const r = {
    marks: at(regionProgram, "uMarks"),
    time: at(regionProgram, "uTime"),
    prevTime: at(regionProgram, "uPrevTime"),
    cell: at(regionProgram, "uCell"),
    gridOrigin: at(regionProgram, "uGridOrigin"),
    startOrigin: at(regionProgram, "uStartOrigin"),
    pxPerUnit: at(regionProgram, "uPxPerUnit"),
    ringStart: at(regionProgram, "uRingStart"),
    ringDrift: at(regionProgram, "uRingDrift"),
    lobes: at(regionProgram, "uLobes"),
  };
  const texture = gl.createTexture();
  const framebuffer = gl.createFramebuffer();
  // A copy of one layer, so the region pass can read it while it writes the layer.
  const copy = gl.createTexture();
  const copyFramebuffer = gl.createFramebuffer();
  const emptyArray = gl.createVertexArray();
  const feedback = gl.createTransformFeedback();
  // Which wave owns each slot, and each slot's GPU state.
  const table = new GrainSlots(GRAIN_SLOTS);
  const slots: (Slot | null)[] = Array.from({ length: GRAIN_SLOTS }, () => null);
  // The grains' grid, the field grid it was fitted to, and the field's offset inside it
  // (`fit()`).
  const grid: GrainGrid = { cols: 0, rows: 0, cellW: 1, cellH: 1, originX: 0, originY: 0 };
  const fitted: GrainGrid = { ...grid };
  const offset = { x: 0, y: 0 };

  function stateArray(program: WebGLProgram, buffer: WebGLBuffer): WebGLVertexArrayObject {
    const vao = gl.createVertexArray();
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    ["aState0", "aState1", "aState2"].forEach((name, i) => {
      const location = gl.getAttribLocation(program, name);
      if (location < 0) return;
      gl.enableVertexAttribArray(location);
      gl.vertexAttribPointer(location, 4, gl.FLOAT, false, STATE_FLOATS * 4, i * 16);
    });
    gl.bindVertexArray(null);
    // A buffer that transform feedback writes to can't stay bound anywhere else.
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    return vao;
  }

  function freeSlot(index: number): void {
    const slot = slots[index];
    if (!slot) return;
    for (const buffer of slot.buffers) gl.deleteBuffer(buffer);
    for (const vao of [...slot.updateArrays, ...slot.markArrays]) gl.deleteVertexArray(vao);
    slots[index] = null;
  }

  // Whether any wave's simulation is running. A wave that starts this frame doesn't count.
  function anyLive(): boolean {
    for (let i = 0; i < GRAIN_SLOTS; i++) if (slots[i]) return true;
    return false;
  }

  // Sizes the texture array and the copy to the grains' grid.
  function allocate(cols: number, rows: number): void {
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, texture);
    gl.texImage3D(
      gl.TEXTURE_2D_ARRAY,
      0,
      gl.RGBA16F,
      cols,
      rows,
      GRAIN_SLOTS,
      0,
      gl.RGBA,
      gl.HALF_FLOAT,
      null,
    );
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.bindTexture(gl.TEXTURE_2D, copy);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16F, cols, rows, 0, gl.RGBA, gl.HALF_FLOAT, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, copyFramebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, copy, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  // The grains' grid follows the field's while no wave is live. While waves are live, it stays
  // put as long as the field's grid still fits inside it with the same cells; otherwise every
  // live wave starts over on a new grid. Returns whether it fitted a new grid, which frees
  // every slot.
  function fit(field: GrainGrid): boolean {
    if (anyLive() || sameGrid(field, fitted)) {
      offset.x = Math.round((field.originX - grid.originX) / field.cellW);
      offset.y = Math.round((field.originY - grid.originY) / field.cellH);
      const fits =
        field.cellW === grid.cellW &&
        field.cellH === grid.cellH &&
        offset.x >= 0 &&
        offset.y >= 0 &&
        offset.x + field.cols <= grid.cols &&
        offset.y + field.rows <= grid.rows;
      if (fits) return false;
    }
    for (let i = 0; i < GRAIN_SLOTS; i++) freeSlot(i);
    Object.assign(fitted, field);
    offset.x = Math.ceil(field.cols * HEADROOM);
    offset.y = Math.ceil(field.rows * HEADROOM);
    const cols = field.cols + 2 * offset.x;
    const rows = field.rows + 2 * offset.y;
    if (cols !== grid.cols || rows !== grid.rows) allocate(cols, rows);
    grid.cols = cols;
    grid.rows = rows;
    grid.cellW = field.cellW;
    grid.cellH = field.cellH;
    grid.originX = field.originX - offset.x * field.cellW;
    grid.originY = field.originY - offset.y * field.cellH;
    return true;
  }

  function bindLayer(layer: number): void {
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.framebufferTextureLayer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, texture, 0, layer);
    gl.viewport(0, 0, grid.cols, grid.rows);
  }

  function startSlot(index: number, ring: GrainWave, viewport: GrainViewport): void {
    const count = grid.cols * grid.rows;
    const farthest = Math.hypot(
      Math.max(Math.abs(ring.originX), Math.abs(viewport.width - ring.originX)),
      Math.max(Math.abs(ring.originY), Math.abs(viewport.height - ring.originY)),
    );
    const pxPerUnit = Math.max(1, farthest) / CORNER_UNITS;
    const state = new Float32Array(count * STATE_FLOATS);
    for (let i = 0; i < count; i++) {
      const x = grid.originX + ((i % grid.cols) + 0.5) * grid.cellW;
      const y = grid.originY + (Math.floor(i / grid.cols) + 0.5) * grid.cellH;
      state[i * STATE_FLOATS] = (x - ring.originX) / pxPerUnit;
      state[i * STATE_FLOATS + 1] = (y - ring.originY) / pxPerUnit;
      state[i * STATE_FLOATS + 11] = hash1(i + 1);
    }
    const buffers: [WebGLBuffer, WebGLBuffer] = [gl.createBuffer(), gl.createBuffer()];
    gl.bindBuffer(gl.ARRAY_BUFFER, buffers[0]);
    gl.bufferData(gl.ARRAY_BUFFER, state, gl.DYNAMIC_COPY);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffers[1]);
    gl.bufferData(gl.ARRAY_BUFFER, state.byteLength, gl.DYNAMIC_COPY);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    // Starts the way the old sweep did: just behind the butterfly, off to one side, drifting
    // with the gust (down the page, within 30 degrees, from the wave's draw).
    const angle = ring.draw.driftAngle;
    const side = ((ring.draw.lobePhase / (2 * Math.PI)) * 2 - 1) * 0.04;
    slots[index] = {
      buffers,
      updateArrays: [stateArray(updateProgram, buffers[0]), stateArray(updateProgram, buffers[1])],
      markArrays: [stateArray(markProgram, buffers[0]), stateArray(markProgram, buffers[1])],
      current: 0,
      time: 0,
      prevTime: 0,
      pxPerUnit,
      startX: ring.originX,
      startY: ring.originY,
      ringStart: [
        -0.12 * Math.cos(angle) - side * Math.sin(angle),
        -0.12 * Math.sin(angle) + side * Math.cos(angle),
      ],
      ringDrift: [0.3 * Math.cos(angle), 0.3 * Math.sin(angle)],
      lobes: [ring.draw.lobeCount, ring.draw.lobePhase],
    };
    bindLayer(index);
    gl.clearColor(UNREACHED, 0, 0, UNREACHED);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  function step(slot: Slot, dt: number, count: number): void {
    gl.useProgram(updateProgram);
    gl.uniform1f(u.time, slot.time);
    gl.uniform1f(u.delta, dt);
    gl.uniform2fv(u.ringStart, slot.ringStart);
    gl.uniform2fv(u.ringDrift, slot.ringDrift);
    gl.enable(gl.RASTERIZER_DISCARD);
    gl.bindVertexArray(slot.updateArrays[slot.current]);
    gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, feedback);
    gl.bindBufferBase(gl.TRANSFORM_FEEDBACK_BUFFER, 0, slot.buffers[slot.current === 0 ? 1 : 0]);
    gl.beginTransformFeedback(gl.POINTS);
    gl.drawArrays(gl.POINTS, 0, count);
    gl.endTransformFeedback();
    gl.bindBufferBase(gl.TRANSFORM_FEEDBACK_BUFFER, 0, null);
    gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, null);
    gl.bindVertexArray(null);
    gl.disable(gl.RASTERIZER_DISCARD);
    slot.current = slot.current === 0 ? 1 : 0;
    slot.time += dt;
  }

  function mark(slot: Slot, index: number, count: number): void {
    bindLayer(index);
    gl.useProgram(markProgram);
    gl.uniform1f(m.time, slot.time);
    gl.uniform1f(m.pxPerUnit, slot.pxPerUnit);
    gl.uniform2f(m.startOrigin, slot.startX, slot.startY);
    gl.uniform2f(m.grid, grid.cols, grid.rows);
    gl.uniform2f(m.cell, grid.cellW, grid.cellH);
    gl.uniform2f(m.gridOrigin, grid.originX, grid.originY);
    gl.uniform1f(m.sweep, SWEEP_UNITS);
    gl.bindVertexArray(slot.markArrays[slot.current]);
    gl.enable(gl.BLEND);
    // A: the earliest time each cell's grain got swept.
    gl.colorMask(false, false, false, true);
    gl.blendEquation(gl.MIN);
    gl.uniform1i(m.mode, 0);
    gl.drawArrays(gl.POINTS, 0, count);
    // G: the dust over each cell right now.
    gl.colorMask(false, true, false, false);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.blendEquation(gl.FUNC_ADD);
    gl.blendFunc(gl.ONE, gl.ONE);
    gl.uniform1i(m.mode, 1);
    gl.drawArrays(gl.POINTS, 0, count);
    gl.colorMask(true, true, true, true);
    gl.disable(gl.BLEND);
    gl.bindVertexArray(null);
    // R and B, from a copy of the layer.
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, framebuffer);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, copyFramebuffer);
    gl.blitFramebuffer(
      0,
      0,
      grid.cols,
      grid.rows,
      0,
      0,
      grid.cols,
      grid.rows,
      gl.COLOR_BUFFER_BIT,
      gl.NEAREST,
    );
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
    bindLayer(index);
    gl.useProgram(regionProgram);
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, copy);
    gl.uniform1i(r.marks, unit);
    gl.uniform1f(r.time, slot.time);
    gl.uniform1f(r.prevTime, slot.prevTime);
    gl.uniform2f(r.cell, grid.cellW, grid.cellH);
    gl.uniform2f(r.gridOrigin, grid.originX, grid.originY);
    gl.uniform2f(r.startOrigin, slot.startX, slot.startY);
    gl.uniform1f(r.pxPerUnit, slot.pxPerUnit);
    gl.uniform2fv(r.ringStart, slot.ringStart);
    gl.uniform2fv(r.ringDrift, slot.ringDrift);
    gl.uniform2fv(r.lobes, slot.lobes);
    gl.bindVertexArray(emptyArray);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.bindVertexArray(null);
    slot.prevTime = slot.time;
    // Put the grains' array back on the unit for the lift pass.
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, texture);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  return {
    texture,
    offset,
    slotOf: (id) => table.slotOf(id),
    timeOf(id) {
      const index = table.slotOf(id);
      return index < 0 ? 0 : (slots[index]?.time ?? 0);
    },
    update(waves, field, viewport) {
      table.update(waves);
      for (const index of table.freed) freeSlot(index);
      // A new grid frees every slot, so each live wave grabs one again and starts over.
      if (fit(field)) {
        table.clear();
        table.update(waves);
      }
      const count = grid.cols * grid.rows;
      for (const wave of waves) {
        const index = table.slotOf(wave.id);
        if (index < 0) continue;
        // A slot with no simulation is new to its wave, so it starts with a cleared layer.
        if (!slots[index]) startSlot(index, wave, viewport);
        const slot = slots[index];
        if (!slot) continue;
        const target = clamp01(wave.life) * SIM_SECONDS;
        const size = Math.max(STEP_SECONDS, (target - slot.time) / FRAME_STEPS);
        // Steps of `size`, plus a shorter last one.
        for (let k = 0; k <= FRAME_STEPS && slot.time < target - 1e-6; k++)
          step(slot, Math.min(size, target - slot.time), count);
        mark(slot, index, count);
      }
    },
  };
}
